/**
 * Admin panel for a lesson's SCORM packages (Task #23).
 *
 * Lists the packages currently linked to the lesson (manifest version,
 * size, upload time) with Replace / Delete actions, and embeds the
 * uploader. "Replace" is the upload flow — the server retires prior
 * packages for the lesson when a new zip is imported.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { ScormPackage } from '@xsc/shared';
import { api } from '../../lib/api-client.js';
import { Badge } from '../ui/badge.js';
import { ScormUpload } from './scorm-upload.js';

export function formatBytes(n: number): string {
  if (!Number.isFinite(n) || n < 0) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

export function manifestLabel(v: ScormPackage['manifest_version']): string {
  switch (v) {
    case 'scorm12':
      return 'SCORM 1.2';
    case 'scorm2004':
      return 'SCORM 2004';
    case 'xapi':
      return 'xAPI / Tin Can';
    default:
      return v;
  }
}

interface Props {
  moduleId: string;
  lessonId: string;
  /** Called after the server has changed the lesson's content blocks
   *  (upload attached a block / delete detached one). Callers should
   *  refresh the lesson from the server. */
  onChanged: () => void;
}

export function ScormPackagesPanel({ moduleId, lessonId, onChanged }: Props): JSX.Element {
  const qc = useQueryClient();
  const queryKey = ['admin', 'scorm', 'lesson', lessonId];

  const q = useQuery({
    queryKey,
    queryFn: () =>
      api.get<{ packages: ScormPackage[] }>(
        `/v1/admin/training/modules/${encodeURIComponent(moduleId)}/lessons/${encodeURIComponent(lessonId)}/scorm-packages`,
      ),
  });

  const del = useMutation({
    mutationFn: (packageId: string) =>
      api.delete<{ ok: true }>(
        `/v1/admin/training/scorm-packages/${encodeURIComponent(packageId)}`,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'scorm'] });
      onChanged();
    },
  });

  const packages = q.data?.packages ?? [];

  return (
    <div className="mt-3 space-y-2">
      {q.isError && (
        <p className="text-xs text-danger">Failed to load SCORM packages.</p>
      )}
      {del.isError && (
        <p className="text-xs text-danger">
          Delete failed: {del.error instanceof Error ? del.error.message : 'unknown error'}
        </p>
      )}
      {packages.length > 0 && (
        <div className="rounded border border-border divide-y divide-border">
          {packages.map((p) => (
            <div key={p.id} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium truncate">{p.title ?? p.id}</span>
                  <Badge tone="neutral">{manifestLabel(p.manifest_version)}</Badge>
                </div>
                <div className="text-xs text-ink-muted">
                  {formatBytes(p.total_bytes)} · {p.total_files} files · uploaded{' '}
                  {new Date(p.uploaded_at).toLocaleString()}
                </div>
              </div>
              <button
                type="button"
                className="shrink-0 text-xs px-2 py-1 rounded border border-border text-danger"
                disabled={del.isPending}
                onClick={() => {
                  if (
                    confirm(
                      `Delete SCORM package "${p.title ?? p.id}"? This removes its block from the lesson and frees its storage.`,
                    )
                  )
                    del.mutate(p.id);
                }}
              >
                {del.isPending && del.variables === p.id ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          ))}
        </div>
      )}
      <ScormUpload
        moduleId={moduleId}
        lessonId={lessonId}
        replace={packages.length > 0}
        onImported={() => {
          void qc.invalidateQueries({ queryKey: ['admin', 'scorm'] });
          onChanged();
        }}
      />
    </div>
  );
}
